/**
 * GFG-194 — the pure parts of `CampaignContactsModal`: which statuses the Status select should
 * offer, which rows survive the search + status filter, and the CSV of whatever survived. Split
 * out of the component so each rule is directly testable
 * (`campaignContacts-helper.test.ts`) without mounting a dialog.
 *
 * It sits beside the component rather than in `src/utils/` because nothing outside this module
 * uses it; promote it to `utils/` the day a second module needs it.
 */
import z from "zod";
import { COPY } from "@/constants/campaignContacts";
import {
  CAMPAIGN_LINK_STATUS_VALUES,
  CAMPAIGN_STATUSES,
  type CampaignLinkStatus,
} from "@/constants/campaings";
import { DATE_LOCALE } from "@/constants/dates";
import type { UpdateCampaignContactBody } from "@/interfaces/campaignContacts";
import { buildCsv, type CsvColumn } from "@/lib/csv";
import {
  isoToZonedSlot,
  resolveScheduleTimeZone,
  scheduleZoneLabel,
} from "@/utils/timezone-helper";
import type { CampaignContactRow, CampaignContactsLock } from "./types";

/**
 * The statuses the Status select offers, built from the rows in hand — the prototype's
 * `cmpRenderContactsFilter` rule: a status nobody has is not offered, so the menu can never
 * produce an empty table. Known statuses come first in `CAMPAIGN_LINK_STATUS_VALUES` order; anything the
 * backend sends that this build doesn't know about is kept (in first-seen order) rather than
 * dropped, which would make its rows unreachable by filter.
 */
export function contactStatusesPresent(
  contacts: readonly Pick<CampaignContactRow, "status">[],
): CampaignLinkStatus[] {
  const present = new Set<CampaignLinkStatus>(contacts.map((c) => c.status));
  const known = CAMPAIGN_LINK_STATUS_VALUES.filter((s) => present.has(s));
  const extra = [...present].filter((s) => !CAMPAIGN_LINK_STATUS_VALUES.includes(s));
  return [...known, ...extra];
}

/**
 * Search (name OR email, case-insensitive, substring) intersected with the status filter, over the
 * list the caller supplied — this modal filters client-side and never re-queries.
 * `status === null` means "All statuses".
 */
export function filterCampaignContacts(
  contacts: CampaignContactRow[],
  search: string,
  status: CampaignLinkStatus | null,
): CampaignContactRow[] {
  const query = search.trim().toLowerCase();
  return contacts.filter((contact) => {
    if (status !== null && contact.status !== status) return false;
    if (!query) return true;
    return (
      contact.name.toLowerCase().includes(query) || contact.email.toLowerCase().includes(query)
    );
  });
}

/** Which optional columns any of these rows can actually fill. */
export interface CampaignContactsColumnFlags {
  delivered: boolean;
  sendDate: boolean;
  url: boolean;
}

/**
 * KEY PRESENCE, NOT TRUTHINESS — the single rule for whether an optional column is drawn, used by
 * both the table and the CSV.
 *
 * A caller whose endpoint has no such field at all omits the key and gets a narrower table. A
 * caller whose endpoint HAS the field shows the column even when every value is null, and those
 * rows render an em dash. Deciding this on truthiness (`Boolean(c.deliveredAt)`, as this function
 * used to) hid the whole DELIVERED column on any campaign whose gift had not gone out yet — which,
 * on the Send Queue, is every campaign in the queue.
 *
 * `url` honours both names for the same field: `campaignUrl` is the wire name, `redemptionUrl` the
 * older alias (see `types.ts`), and reading only one would blank the column for half the callers.
 */
export function campaignContactsColumns(
  contacts: CampaignContactRow[],
): CampaignContactsColumnFlags {
  return {
    delivered: contacts.some((c) => c.deliveredAt !== undefined),
    sendDate: contacts.some((c) => c.sendDate !== undefined),
    url: contacts.some((c) => c.campaignUrl !== undefined || c.redemptionUrl !== undefined),
  };
}

/** Table columns drawn: First Name, Last Name, Email and Status, plus the optional ones and actions. */
export function contactTableColumnCount(
  columns: CampaignContactsColumnFlags,
  showActions: boolean,
): number {
  return (
    4 +
    (columns.delivered ? 1 : 0) +
    (columns.sendDate ? 1 : 0) +
    (columns.url ? 1 : 0) +
    (showActions ? 1 : 0)
  );
}

/**
 * The campaign URL for a row, from whichever of the two names the caller set.
 */
export function contactUrl(contact: CampaignContactRow): string | null {
  return contact.campaignUrl ?? contact.redemptionUrl ?? null;
}

/**
 * 07/14/2026 — the DELIVERED column's date, READ IN UTC.
 *
 * `utils/date-format`'s `formatDate` renders the same instant in the viewer's local zone, so a
 * `2026-07-14T02:00:00Z` delivery shows as the 13th anywhere west of Greenwich and the column
 * disagrees with every other system reporting that send. These timestamps are UTC instants
 * (`contacts.sent_at`), and the prototype reads them with `getUTCDate()` (`cmpFmtDate`), so this
 * column pins `timeZone: "UTC"` and matches. The shared helper is deliberately left alone: it is
 * used across the whole app, where a local-time reading is often the right one.
 *
 * A send date or a budget-history date passes the campaign's zone instead, and reads as that zone's
 * calendar day.
 */
export function formatContactDate(iso: string | null | undefined, timeZone = "UTC"): string {
  if (!iso) return COPY.emptyValue;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return COPY.emptyValue;
  return date.toLocaleDateString(DATE_LOCALE, {
    timeZone,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

/**
 * 11/03/2026, 7:00 AM Pacific (PT) — a send instant read in the campaign's zone, with its label:
 * the SEND DATE column of a multi-date campaign and the wizard's send-time text. The DELIVERED
 * column keeps `formatContactDate`.
 */
export function formatSendInstant(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return COPY.emptyValue;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return COPY.emptyValue;
  const when = date.toLocaleString(DATE_LOCALE, {
    timeZone,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return `${when} ${scheduleZoneLabel(timeZone)}`;
}

/**
 * An instant's calendar day (YYYY-MM-DD) in the campaign's zone, for a date input's value or
 * bounds; undefined when there is no usable instant. Slicing the UTC ISO string instead gives the
 * next day on any evening west of Greenwich.
 */
export function zonedDateInput(
  iso: string | null | undefined,
  timeZone: string,
): string | undefined {
  if (!iso || Number.isNaN(Date.parse(iso))) return undefined;
  return isoToZonedSlot(iso, timeZone).date;
}

export const DAY_MS = 24 * 60 * 60 * 1000;

/** A YYYY-MM-DD calendar day moved by `days` whole days. */
function shiftCalendarDay(day: string, days: number): string {
  // Calendar arithmetic on a UTC midnight, where no DST shift makes a day longer or shorter.
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** The earliest send date an admin may pick: the day after today in the campaign's zone (YYYY-MM-DD). */
export function minSendDateInput(timeZone: string, now: Date = new Date()): string {
  return shiftCalendarDay(isoToZonedSlot(now.toISOString(), timeZone).date, 1);
}

/**
 * The latest send date an admin may pick: the day before the close instant's calendar day in the
 * campaign's zone (YYYY-MM-DD), since the close day itself is refused. Undefined with no usable close.
 */
export function maxSendDateInput(
  expireDate: string | null | undefined,
  timeZone: string,
): string | undefined {
  const closeDay = zonedDateInput(expireDate, timeZone);
  return closeDay === undefined ? undefined : shiftCalendarDay(closeDay, -1);
}

/**
 * Whether `date` is a strict YYYY-MM-DD naming a real calendar day. Round-trips through `Date.UTC`
 * as the API's `isFutureSendDate` does, so an impossible day (Nov 31, month 13) is refused here too.
 */
export function isCalendarDateInput(date: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const utc = Date.UTC(
    Number(date.slice(0, 4)),
    Number(date.slice(5, 7)) - 1,
    Number(date.slice(8, 10)),
  );
  return new Date(utc).toISOString().slice(0, 10) === date;
}

/** Whether `date` is a real YYYY-MM-DD day on or after `minSendDateInput(timeZone, now)`. */
export function isFutureSendDateInput(
  date: string,
  timeZone: string,
  now: Date = new Date(),
): boolean {
  return isCalendarDateInput(date) && date >= minSendDateInput(timeZone, now);
}

/** The comparison key for an email: trimmed and lower-cased. */
export function contactEmailKey(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * The hover hint for a name or email cell: the campaign lock first, then the contact's status.
 * A Pending or Bounced field gets the edit hint only when `editable` (the server's per-row
 * `allowed.editName` / `allowed.editEmail`), otherwise none; other statuses get the read-only text.
 */
export function contactFieldHint(
  field: "name" | "email",
  status: CampaignLinkStatus,
  lock: CampaignContactsLock | null,
  name: string,
  editable: boolean,
): string | null {
  if (lock !== null) return COPY.actionBlocked[lock];
  const {
    LINK_PENDING: PENDING,
    LINK_BOUNCED: BOUNCED,
    LINK_REDEEMED: REDEEMED,
  } = CAMPAIGN_STATUSES;
  if (field === "name") {
    if (status === PENDING) return editable ? COPY.fieldHint.name.pending : null;
    if (status === BOUNCED) return editable ? COPY.fieldHint.name.bounced : null;
    return COPY.fieldHint.name.locked;
  }
  if (status === PENDING) return editable ? COPY.fieldHint.email.pending : null;
  if (status === BOUNCED) return editable ? COPY.fieldHint.email.bounced : null;
  return status === REDEEMED ? COPY.fieldHint.email.redeemed(name) : COPY.fieldHint.email.sent;
}

/**
 * The prototype's `cmpResendable`: resending is only meaningful for a gift that went out and was
 * not claimed — `Bounced`, plus a delivered-but-unredeemed state.
 *
 * Matched as plain strings rather than against `CampaignLinkStatus` because the vocabularies differ: the
 * prototype's `Delivered`/`Opened` are this build's `Sent`, the status list is still an open
 * product question (see `CampaignLinkStatusChip`), and a backend that starts sending `Delivered` should
 * behave, not silently lose its resend control.
 */
export const RESENDABLE_STATUSES: readonly string[] = ["Sent", "Delivered", "Opened", "Bounced"];

/** The chip label: a Sent gift whose email was opened reads Opened; the status itself is unchanged. */
export function displayStatus(contact: CampaignContactRow): string {
  return contact.status === "Sent" && contact.openedAt ? "Opened" : contact.status;
}

/** The status whose gift is already claimed, so the contact can no longer be removed. */
export const REDEEMED_STATUS = "Redeemed";

/**
 * Statuses from which a contact can still be removed. Redeemed is claimed; Declined and Expired are
 * over — GFG-364 locks them like Redeemed. Delivered/Opened are the prototype's names for Sent.
 */
export const REMOVABLE_STATUSES: readonly string[] = [
  "Pending",
  "Sent",
  "Delivered",
  "Opened",
  "Bounced",
];

/** The prototype's `CMP_RESEND_MS` — one resend per recipient per 24 hours. */
export const RESEND_COOLDOWN_MS = DAY_MS;

/** Whether this recipient's gift email can be resent at all, ignoring the cooldown. */
export function isResendable(contact: CampaignContactRow): boolean {
  return RESENDABLE_STATUSES.includes(contact.status);
}

/** Whether this recipient can be removed from the campaign at all. */
export function isRemovable(contact: CampaignContactRow): boolean {
  return REMOVABLE_STATUSES.includes(contact.status);
}

/**
 * Whole hours left on the 24-hour cooldown, rounded up as the prototype does (so "0h" is never
 * shown while the wait is still on), or 0 when there is no cooldown running. `now` is a parameter
 * so the rule is testable without freezing the clock.
 */
export function resendCooldownHoursLeft(
  contact: CampaignContactRow,
  now: number = Date.now(),
): number {
  if (!contact.resentAt) return 0;
  const resentAt = new Date(contact.resentAt).getTime();
  if (Number.isNaN(resentAt)) return 0;
  const remaining = RESEND_COOLDOWN_MS - (now - resentAt);
  return remaining > 0 ? Math.ceil(remaining / 3_600_000) : 0;
}

/**
 * Whether `email` is an address the gift can be resent to. Uses zod's email validator on the trimmed
 * value, the same check the API's `dto/campaignContacts.ts` applies to the email it receives: the UI
 * must not accept what the API refuses, or the admin would pass this check and then be rejected.
 */
export function isValidContactEmail(email: string): boolean {
  return z.email().safeParse(email.trim()).success;
}

/**
 * CSV of exactly the rows passed in — the caller passes the *filtered* rows, matching the
 * prototype's `exportContactsCsv`, which exports what the table is showing and not the whole
 * campaign. Escaping and quoting are `lib/csv`'s `buildCsv`; there is no second CSV builder here.
 * Send Date is the calendar day in the campaign's `timeZone` (omitted: Eastern, the business
 * default, as in `CampaignContactsModal`); Delivered stays a UTC day.
 */
export function buildCampaignContactsCsv(
  contacts: CampaignContactRow[],
  flags: CampaignContactsColumnFlags,
  timeZone = resolveScheduleTimeZone(null),
): string {
  const columns: CsvColumn<CampaignContactRow>[] = [
    { label: COPY.columns.name, value: (row) => row.name },
    { label: COPY.columns.email, value: (row) => row.email },
    { label: COPY.columns.status, value: (row) => row.status },
  ];
  if (flags.delivered) {
    columns.push({
      label: COPY.columns.delivered,
      // Empty, not the table's em-dash: a spreadsheet cell should be blank when there is no date.
      value: (row) => (row.deliveredAt ? formatContactDate(row.deliveredAt) : ""),
    });
  }
  if (flags.sendDate) {
    columns.push({
      label: COPY.columns.sendDate,
      value: (row) => (row.sendDate ? formatContactDate(row.sendDate, timeZone) : ""),
    });
  }
  if (flags.url) {
    columns.push({ label: COPY.columns.url, value: (row) => contactUrl(row) ?? "" });
  }
  return buildCsv(columns, contacts);
}

/**
 * What the success toast says after an inline save. A name edit (a body carrying a first or last
 * name) names the new full name, falling back to the contact's name when the body has no first name
 * or the name is blank, so the toast never names nobody; an email edit names the contact; the
 * send-date sentence needs a `sendDate`; any other body says the contact was updated. Precedence
 * when a body carries several: name, email, send date.
 */
export function contactSavedToast(body: UpdateCampaignContactBody, contactName: string): string {
  if (body.firstName !== undefined || body.lastName !== undefined) {
    if (body.firstName === undefined) return COPY.toast.nameUpdated(contactName);
    const fullName = [body.firstName, body.lastName ?? ""]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(" ");
    return COPY.toast.nameUpdated(fullName || contactName);
  }
  if (body.email !== undefined) return COPY.toast.emailUpdated(contactName);
  if (body.sendDate !== undefined) return COPY.toast.sendDateUpdated(contactName);
  return COPY.toast.contactUpdated(contactName);
}
